import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api, API, getToken, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { toast } from "sonner";
import { Copy, Download, Eye, Megaphone, FileText, Square } from "lucide-react";

const FRONTEND_URL = window.location.origin;

const FORMATS = [
  {
    id: "affiche",
    label: "Affiche A4",
    size: "210 × 297 mm",
    desc: "Format vertical pour vitrine, comptoir ou cadre mural.",
    Icon: Megaphone,
    aspect: "aspect-[210/297]",
    accent: "bg-[#397DFF]",
  },
  {
    id: "flyer",
    label: "Flyer A5",
    size: "148 × 210 mm",
    desc: "À distribuer en main propre ou à laisser sur le comptoir.",
    Icon: FileText,
    aspect: "aspect-[148/210]",
    accent: "bg-[#F9C80E]",
  },
  {
    id: "chevalet",
    label: "Chevalet de table",
    size: "210 × 148 mm (paysage)",
    desc: "À plier en deux et poser sur un comptoir, visible des deux côtés.",
    Icon: Square,
    aspect: "aspect-[210/148]",
    accent: "bg-[#10D183]",
  },
];

function AfficheMockup({ merchantNom }) {
  return (
    <div className="relative w-full h-full bg-white overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-2 pt-2 pb-1">
        <div className="font-display font-bold text-[#191D1E] text-[8px] leading-tight">Place<br/>des Énergies</div>
        <div className="bg-[#F9C80E] text-[#191D1E] text-[5px] font-bold px-1.5 py-0.5 rounded-sm rotate-[-2deg]">★ PARTENAIRE</div>
      </div>
      {/* hero row */}
      <div className="flex h-[42%] px-2 gap-1">
        <div className="flex-1 flex flex-col justify-between py-1">
          <div>
            <div className="text-[5px] text-[#687175] mb-0.5">◆ Énergie + commerce local</div>
            <div className="font-display font-bold text-[#191D1E] text-[8px] leading-tight">
              Votre boulanger &amp;<br/>PDE vous offrent <span className="text-[#F9C80E]">30€</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 rounded-full bg-[#F9C80E] flex flex-col items-center justify-center shadow-sm">
              <div className="font-display font-bold text-[#191D1E] text-[6px] leading-none">30€</div>
              <div className="font-display font-bold text-[#191D1E] text-[3px] tracking-wider">CASHBACK</div>
            </div>
            <div className="text-[4px] text-[#191D1E] leading-tight flex-1">soit 30€ offerts chez votre commerce.</div>
          </div>
        </div>
        <div className="flex-1 bg-gradient-to-br from-[#C19A6B] via-[#A87C50] to-[#7A5530] rounded-sm" />
      </div>
      {/* steps */}
      <div className="flex gap-1 px-2 mt-1">
        {[1, 2, 3].map((n) => (
          <div key={n} className="flex-1 flex items-start gap-0.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#397DFF] flex items-center justify-center text-white font-bold text-[5px] flex-shrink-0">{n}</div>
            <div className="h-1 bg-[#E5E7EB] rounded-sm flex-1 mt-1" />
          </div>
        ))}
      </div>
      {/* ambre band */}
      <div className="bg-[#F9C80E] text-[#191D1E] text-[5px] font-bold px-2 py-1 mt-1">⚡ Économies + 30 € offerts</div>
      {/* dark band */}
      <div className="bg-[#191D1E] flex items-center gap-1 px-2 py-1">
        <div className="flex-1">
          <div className="text-[#F9C80E] text-[5px] font-bold">Demandez à être rappelé·e</div>
          <div className="text-white text-[4px] mt-0.5 truncate">Indiquez « {merchantNom || "votre commerce"} »</div>
        </div>
        <div className="w-5 h-5 bg-white rounded-sm grid grid-cols-3 grid-rows-3 gap-px p-0.5">
          {[...Array(9)].map((_, i) => <div key={i} className={`${(i*3+7)%5<3 ? 'bg-[#191D1E]' : 'bg-white'}`} />)}
        </div>
      </div>
      {/* legal */}
      <div className="px-2 py-1 text-[3px] text-[#687175] leading-tight">Avantage de 30 € offert et financé par Place des Énergies, sous réserve de souscription effective. Filiale Selectra.</div>
    </div>
  );
}

function FlyerMockup({ merchantNom }) {
  return (
    <div className="relative w-full h-full bg-white overflow-hidden">
      <div className="relative h-[28%] bg-gradient-to-br from-[#C19A6B] via-[#A87C50] to-[#7A5530]">
        <div className="absolute top-1 left-1 bg-white text-[#191D1E] text-[5px] font-bold px-1 py-0.5 rounded-sm">PDE</div>
        <div className="absolute top-1 right-1 bg-[#F9C80E] text-[#191D1E] text-[4px] font-bold px-1 py-0.5 rounded-sm rotate-[-2deg]">★ PARTENAIRE</div>
      </div>
      <div className="p-2">
        <div className="text-[4px] text-[#687175]">◆ Énergie moins chère</div>
        <div className="font-display font-bold text-[#191D1E] text-[7px] leading-tight mt-0.5">Votre commerce &amp; PDE vous offrent <span className="text-[#F9C80E]">30€</span></div>
        <div className="flex items-center gap-1 mt-1.5">
          <div className="w-5 h-5 rounded-full bg-[#F9C80E] flex flex-col items-center justify-center">
            <div className="font-display font-bold text-[#191D1E] text-[5px]">30€</div>
            <div className="text-[#191D1E] text-[2.5px] font-bold">CASHBACK</div>
          </div>
          <div className="text-[3.5px] text-[#191D1E] leading-tight flex-1">30€ offerts chez votre boulanger.</div>
        </div>
        <div className="mt-1 space-y-0.5">
          {[1,2,3].map(n => (
            <div key={n} className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-[#397DFF] text-white text-[3px] font-bold flex items-center justify-center flex-shrink-0">{n}</div>
              <div className="h-0.5 bg-[#E5E7EB] flex-1 rounded-sm" />
            </div>
          ))}
        </div>
      </div>
      <div className="bg-[#F9C80E] text-[#191D1E] text-[4px] font-bold px-2 py-0.5">⚡ + 30 € offerts</div>
      <div className="bg-[#191D1E] flex items-center gap-1 px-2 py-1">
        <div className="text-white text-[3px] flex-1 truncate">« {merchantNom || "commerce"} »</div>
        <div className="w-3 h-3 bg-white rounded-sm grid grid-cols-3 grid-rows-3 gap-px p-px">
          {[...Array(9)].map((_, i) => <div key={i} className={`${(i+1)%3===0 ? 'bg-[#191D1E]' : i%2===0 ? 'bg-[#191D1E]':'bg-white'}`} />)}
        </div>
      </div>
    </div>
  );
}

function ChevaletMockup({ merchantNom }) {
  const Face = () => (
    <div className="flex-1 bg-[#F5F6F9] p-2 flex flex-col items-center relative">
      <div className="absolute top-1 left-1 text-[#191D1E] text-[4px] font-bold">PDE</div>
      <div className="absolute top-1 right-1 bg-[#F9C80E] text-[#191D1E] text-[3px] font-bold px-1 py-0.5 rounded-sm rotate-[-2deg]">★ PARTENAIRE</div>
      <div className="w-8 h-8 rounded-full bg-[#F9C80E] flex flex-col items-center justify-center mt-3">
        <div className="font-display font-bold text-[#191D1E] text-[8px]">30€</div>
        <div className="text-[#191D1E] text-[3px] font-bold">CASHBACK</div>
      </div>
      <div className="text-center font-display font-bold text-[#191D1E] text-[5px] mt-1.5 leading-tight">Votre commerce<br/>vous offre 30€</div>
      <div className="text-[#191D1E] text-[3px] mt-0.5 truncate max-w-full">chez {merchantNom || "commerce"}</div>
      <div className="w-5 h-5 bg-white rounded-sm grid grid-cols-3 grid-rows-3 gap-px p-px mt-1.5">
        {[...Array(9)].map((_, i) => <div key={i} className={`${(i*3+1)%4<2 ? 'bg-[#191D1E]' : 'bg-white'}`} />)}
      </div>
    </div>
  );
  return (
    <div className="relative w-full h-full bg-white overflow-hidden flex">
      <Face />
      <div className="absolute left-1/2 top-1 bottom-1 w-px border-l border-dashed border-[#687175] opacity-60" />
      <Face />
    </div>
  );
}

function MockupCard({ format, merchantNom }) {
  const Component =
    format.id === "affiche" ? AfficheMockup :
    format.id === "flyer" ? FlyerMockup :
    ChevaletMockup;
  return (
    <div
      className={`relative ${format.aspect} w-full rounded-xl border border-[#E5E7EB] bg-white overflow-hidden shadow-sm`}
      data-testid={`kit-mockup-${format.id}`}
    >
      <Component merchantNom={merchantNom} />
    </div>
  );
}

export function MarketingKitPanel({ merchantId: forcedId = null, allowSelector = true }) {
  const [merchants, setMerchants] = useState([]);
  const [selectedId, setSelectedId] = useState(forcedId || "");
  const [loading, setLoading] = useState(true);
  const [downloadingFmt, setDownloadingFmt] = useState(null);
  const [previewFmt, setPreviewFmt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (forcedId) {
          const { data } = await api.get("/me/merchant");
          if (cancelled) return;
          setMerchants([data]);
          setSelectedId(data.id);
        } else {
          const { data } = await api.get("/merchants");
          if (cancelled) return;
          setMerchants(data || []);
          if ((data || []).length > 0) setSelectedId((data[0] || {}).id || "");
        }
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [forcedId]);

  const selectedMerchant = useMemo(
    () => merchants.find((m) => m.id === selectedId) || null,
    [merchants, selectedId]
  );

  const qrUrl = selectedMerchant ? `${FRONTEND_URL}/?ref=${selectedMerchant.id}` : "";

  const slug = (s) => (s || "commerce").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();

  const download = async (fmt) => {
    if (!selectedMerchant) return;
    setDownloadingFmt(fmt);
    try {
      const res = await axios.get(`${API}/merchants/${selectedMerchant.id}/kit/${fmt}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `PDE_${fmt}_${slug(selectedMerchant.nom)}_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Téléchargement de ${fmt} lancé`);
    } catch (err) {
      toast.error("Téléchargement impossible (vérifiez vos droits sur ce commerçant).");
    } finally {
      setDownloadingFmt(null);
    }
  };

  // Native anchor URL (works around popup blockers — user-gesture nav)
  const previewUrl = (fmt) => {
    if (!selectedMerchant) return "#";
    return `${API}/merchants/${selectedMerchant.id}/kit/preview/${fmt}?token=${encodeURIComponent(getToken() || "")}`;
  };

  const closePreview = () => {
    setPreviewFmt(null);
  };

  return (
    <div
      className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7"
      data-testid="marketing-kit-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#191D1E] tracking-tight">
            Kit marketing
          </h2>
          <p className="text-sm text-[#687175] mt-1 max-w-xl">
            Téléchargez vos supports imprimables personnalisés. Chaque support contient un QR code qui amène
            le client sur votre page d&apos;inscription dédiée.
          </p>
        </div>

        {allowSelector && !forcedId && (
          <div className="min-w-[260px]" data-testid="kit-merchant-selector-wrap">
            <label className="text-xs uppercase tracking-wider text-[#939BA0] font-semibold">
              Commerçant ciblé
            </label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="kit-merchant-selector">
                <SelectValue placeholder={loading ? "Chargement…" : "Choisir un commerçant"} />
              </SelectTrigger>
              <SelectContent>
                {merchants.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`kit-merchant-opt-${m.id}`}>
                    {m.nom} · {m.ville}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {selectedMerchant && (
        <div
          className="rounded-xl bg-[#F5F6F9] border border-[#E5E7EB] p-4 mb-6 flex items-center gap-3 flex-wrap"
          data-testid="kit-qr-banner"
        >
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-wider text-[#939BA0] font-semibold mb-1">
              URL encodée dans le QR code
            </div>
            <div className="text-sm font-mono text-[#191D1E] break-all" data-testid="kit-qr-url">
              {qrUrl}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg"
            data-testid="kit-qr-copy"
            onClick={() => {
              navigator.clipboard.writeText(qrUrl);
              toast.success("URL copiée");
            }}
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#687175] py-10 text-center" data-testid="kit-loading">
          Chargement des commerçants…
        </div>
      ) : !selectedMerchant ? (
        <div className="text-sm text-[#687175] py-10 text-center" data-testid="kit-empty">
          Aucun commerçant disponible.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="kit-cards">
          {FORMATS.map((f) => (
            <div
              key={f.id}
              className="rounded-2xl border border-[#E5E7EB] p-5 bg-white flex flex-col gap-4"
              data-testid={`kit-card-${f.id}`}
            >
              <MockupCard format={f} merchantNom={selectedMerchant.nom} />
              <div>
                <div className="font-display text-lg font-bold text-[#191D1E]">{f.label}</div>
                <div className="text-xs text-[#939BA0] font-mono mt-0.5">{f.size}</div>
                <p className="text-sm text-[#687175] mt-2 leading-snug">{f.desc}</p>
              </div>
              <div className="flex gap-2 mt-auto">
                <Button
                  onClick={() => download(f.id)}
                  disabled={downloadingFmt === f.id}
                  className="flex-1 bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl"
                  data-testid={`kit-download-${f.id}`}
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {downloadingFmt === f.id ? "…" : "Télécharger"}
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-xl"
                  data-testid={`kit-preview-${f.id}`}
                >
                  <a
                    href={previewUrl(f.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Eye className="w-4 h-4 mr-1.5" />
                    Aperçu
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MarketingKitPanel;
